/**
 * Hooks React Query du joueur DAG.
 *
 * Lecture/gating + démarrage/finalisation = RPC security definer ; soumission
 * d'une réponse = edge fn `submit-dag-answer` (le serveur est seul juge). Les RPC
 * et l'edge fn sont POSTÉRIEURES à types.generated.ts (stale volontaire,
 * cf. CLAUDE.md) → handle non-typé + cast `as unknown as`.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type {
  CompleteAttemptResult,
  PlayerDagResult,
  StartAttemptResult,
  SubmitAnswerInput,
  SubmitAnswerResult,
} from './types';

const db = supabase as unknown as SupabaseClient;

export const dagPlayerQueryKey = (exerciseId: string) => ['dag-player', exerciseId] as const;

export function useDagForPlayer(exerciseId: string | null | undefined) {
  return useQuery({
    queryKey: dagPlayerQueryKey(exerciseId ?? ''),
    enabled: !!exerciseId,
    queryFn: async (): Promise<PlayerDagResult> => {
      const { data, error } = await db.rpc('get_dag_for_player', { p_exercise_id: exerciseId });
      if (error) throw new Error(error.message);
      return data as unknown as PlayerDagResult;
    },
  });
}

export function useStartDagAttempt() {
  return useMutation({
    mutationFn: async (exerciseId: string): Promise<StartAttemptResult> => {
      const { data, error } = await db.rpc('start_dag_attempt', { p_exercise_id: exerciseId });
      if (error) throw new Error(error.message);
      return data as unknown as StartAttemptResult;
    },
  });
}

export function useSubmitDagAnswer() {
  return useMutation({
    mutationFn: async (body: SubmitAnswerInput): Promise<SubmitAnswerResult> => {
      const { data, error } = await supabase.functions.invoke<SubmitAnswerResult>(
        'submit-dag-answer',
        { body },
      );
      if (error) {
        let message = error.message;
        const ctx = (error as Error & { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = (await ctx.json()) as { error?: string; message?: string };
            message = parsed.message ?? parsed.error ?? message;
          } catch {
            /* garde le message générique */
          }
        }
        throw new Error(message);
      }
      if (!data) throw new Error('empty_response');
      return data;
    },
  });
}

export function useCompleteDagAttempt() {
  return useMutation({
    mutationFn: async (attemptId: string): Promise<CompleteAttemptResult> => {
      const { data, error } = await db.rpc('complete_dag_attempt', { p_attempt_id: attemptId });
      if (error) throw new Error(error.message);
      return data as unknown as CompleteAttemptResult;
    },
  });
}
